/**
 * Locations module permissions.
 *
 * Capability = Admin or LocationsAdmin (all Location.* rights, migration 261), or the matching
 * `Location.*` right (rights can be granted per user without a role). The "Locations Access"
 * role carries the read rights, so it also opens every read surface by default.
 *
 * Member names, member search and "Members at this campus" come from MemberManagement and need
 * its own rights (Member.Read, or Member.Location.Read for the campus list).
 *
 * The UI only hides what the user cannot do; the API enforces the same rules.
 */

import { useCallback, useMemo } from 'react';
import { LOCATION_RIGHTS, LOCATION_ROLES } from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '../useEnrichedAuth';

export { LOCATION_RIGHTS, LOCATION_ROLES };

/** Roles that open the Locations module (the domain gate). */
export const LOCATIONS_MODULE_ROLES: string[] = [
  LOCATION_ROLES.Admin,
  LOCATION_ROLES.LocationsAdmin,
  LOCATION_ROLES.LocationsAccess,
];

export function useLocationsPermissions() {
  const { user, hasRole, hasPermission } = useEnrichedAuth();

  const isAdmin = hasRole(LOCATION_ROLES.Admin);
  const isLocationsAdmin = isAdmin || hasRole(LOCATION_ROLES.LocationsAdmin);
  const isReader = hasRole(LOCATION_ROLES.LocationsAccess);

  /** Admin / LocationsAdmin, or (for read rights) the "Locations Access" role, or the explicit right. */
  const can = useCallback(
    (right: string, readerDefault = false) => isLocationsAdmin || (readerDefault && isReader) || hasPermission(right),
    [isLocationsAdmin, isReader, hasPermission]
  );

  const perms = useMemo(() => {
    const R = LOCATION_RIGHTS;
    const canView = can(R.View, true);
    const canViewSocialCredentials = can(R.SocialMediaAccountView);
    return {
      // Locations
      canView,
      canCreate: can(R.Create),
      canUpdate: can(R.Update),
      /** Archive + restore. */
      canArchive: can(R.Delete),
      // Address
      canViewAddress: can(R.AddressView, true),
      canCreateAddress: can(R.AddressCreate),
      canUpdateAddress: can(R.AddressUpdate),
      canDeleteAddress: can(R.AddressDelete),
      // Service times
      canViewServiceTimes: can(R.ServiceTimeView, true),
      canCreateServiceTime: can(R.ServiceTimeCreate),
      canUpdateServiceTime: can(R.ServiceTimeUpdate),
      canArchiveServiceTime: can(R.ServiceTimeDelete),
      // Venues
      canViewVenues: can(R.VenueView, true),
      canCreateVenue: can(R.VenueCreate),
      canUpdateVenue: can(R.VenueUpdate),
      canArchiveVenue: can(R.VenueDelete),
      // Leaders
      canViewLeaders: can(R.LeaderView, true),
      canAddLeader: can(R.LeaderCreate),
      canUpdateLeader: can(R.LeaderUpdate),
      canRemoveLeader: can(R.LeaderDelete),
      // Additional info
      canViewInfo: can(R.InfoView, true),
      canCreateInfo: can(R.InfoCreate),
      canUpdateInfo: can(R.InfoUpdate),
      canDeleteInfo: can(R.InfoDelete),
      // Social media platforms (lookup)
      canViewPlatforms: can(R.SocialMediaView, true),
      canCreatePlatform: can(R.SocialMediaCreate),
      canUpdatePlatform: can(R.SocialMediaUpdate),
      canDeletePlatform: can(R.SocialMediaDelete),
      // Social accounts per location
      canViewSocialPublic: canViewSocialCredentials || can(R.SocialMediaAccountViewPublic, true) || canView,
      canViewSocialCredentials,
      canCreateSocial: can(R.SocialMediaAccountCreate),
      canUpdateSocial: can(R.SocialMediaAccountUpdate),
      canDeleteSocial: can(R.SocialMediaAccountDelete),
    };
  }, [can]);

  // MemberManagement rights (names, search, campus member list).
  const canReadMembers = isAdmin || hasPermission('Member.Read');
  const canViewCampusMembers = canReadMembers || hasPermission('Member.Location.Read');
  /** Meetings admin venue pages (set-ups, layouts, blackouts) are gated on Meeting.Admin. */
  const canManageMeetingVenues = isAdmin || hasPermission('Meeting.Admin');

  /** Any write at all (used to word read-only notices). */
  const canWriteAnything =
    perms.canCreate ||
    perms.canUpdate ||
    perms.canArchive ||
    perms.canCreateAddress ||
    perms.canUpdateAddress ||
    perms.canCreateServiceTime ||
    perms.canUpdateServiceTime ||
    perms.canCreateVenue ||
    perms.canUpdateVenue ||
    perms.canAddLeader ||
    perms.canUpdateLeader ||
    perms.canCreateInfo ||
    perms.canUpdateInfo ||
    perms.canCreateSocial ||
    perms.canUpdateSocial ||
    perms.canCreatePlatform ||
    perms.canUpdatePlatform;

  return {
    ...perms,
    isAdmin,
    isLocationsAdmin,
    isReader,
    canReadMembers,
    canViewCampusMembers,
    canManageMeetingVenues,
    canWriteAnything,
    /** May open the module at all. */
    canOpenModule: perms.canView,
    userId: user?.id as string | undefined,
  };
}

export type LocationsPermissions = ReturnType<typeof useLocationsPermissions>;
